import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { flexibleBooleanFromString } from './common-utils.js';

/** ISO date strings or Temporal.Instant (e.g. serialized API payloads vs internal shapes). */
export const dateStringOrInstant = z.union([z.string(), z.instanceof(Temporal.Instant)]);

/** Parseable by `Temporal.PlainDate.from` (calendar date, typically YYYY-MM-DD). */
export const plainDateStringSchema = z.string().refine(
  (value) => {
    try {
      Temporal.PlainDate.from(value);
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Must be a calendar date (YYYY-MM-DD)' },
);

/**
 * Optional PlainDate for URL search / query filters.
 * Invalid or missing values become `undefined` so malformed params do not crash renderers.
 */
export const optionalPlainDateStringSchema = plainDateStringSchema.optional().catch(undefined);

/**
 * Formats a Zod safeParse() error for display (e.g. CSV row validation).
 * Includes every issue and the field/column path so users know what to fix.
 */
export function getZodErrorMessage(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const column = issue.path.map(String).join(' → ');
      return column ? `${column}: ${issue.message}` : issue.message;
    })
    .join('; ');
}

/**
 * Formats a Zod error for logs and upstream-integration failures (e.g. Airtable sync).
 * Unlike {@link getZodErrorMessage}, it spells out the expected and received types:
 * Zod's default "Required" does not say which shape the payload should have had.
 */
export function getZodErrorDetail(error: z.ZodError): string {
  return error.issues
    .map((issue) => `${issue.path.map(String).join(' → ') || '(root)'}: ${issueDetail(issue)}`)
    .join('; ');
}

function issueDetail(issue: z.ZodIssue): string {
  if (issue.code !== z.ZodIssueCode.invalid_type) {
    return issue.message;
  }
  return issue.received === 'undefined'
    ? `missing, expected ${issue.expected}`
    : `expected ${issue.expected}, got ${issue.received}`;
}

export const nonEmptyString = z.string({ message: 'Required' }).min(1, {
  message: 'Required',
});
export const booleanString = z.enum(['true', 'false'], { message: 'Required' });
export const intString = z.string({ message: 'Required' }).regex(/^\d+$/, {
  message: 'Please enter a valid number.',
});

export const optionalEmail = z
  .string()
  .transform((val) => (!val ? undefined : val))
  .pipe(z.string().email({ message: 'Invalid email format' }).optional())
  .optional();

/**
 * Flexible boolean schema that accepts various string formats for CSV parsing.
 * Accepts: null, empty string, TRUE, FALSE, False, True, true, false
 */
export const flexibleBoolean = z
  .string()
  .nullable()
  .transform((val) => flexibleBooleanFromString(val))
  .pipe(z.boolean().nullable());
